/**
 * Strategy Types for Best-Fit Processing
 *
 * Defines the interfaces and types for the extensible strategy pattern used in
 * best-fit processing. This allows different project types to have their own
 * viability criteria, selection logic, and search algorithms while sharing
 * common processing engines.
 */

import type { SimplifiedProjectData } from '~/engine/millage/shape-generators/general-processing';
import type { Candidate, Configuration, Project } from '~/types';
import type { BaseProjectElements } from '~/types/candidate';

/**
 * Project types supported by the best-fit processing system.
 * Extensible design allows for future project types.
 */
export enum ProjectType {
  MAX_VALUE = 'max-value', // Maximize total principal capacity
  TARGET_VALUE = 'target-value', // Achieve target efficiently
  // Future types can be added here:
  // BALANCED = "balanced",
  // RISK_AVERSE = "risk-averse",
  // COMPLIANCE = "compliance"
}

/**
 * Direction for bidirectional search algorithms
 */
export type SearchDirection = 'forward' | 'backward';

/**
 * Context information passed to search strategies
 */
export interface SearchContext {
  baseElements: BaseProjectElements;
  configuration: Configuration;
  projectData: SimplifiedProjectData; // SimplifiedProjectData with proper dateInputs
  project: Project; // Full project data needed for candidate processing
}

/**
 * Common search options across all strategies
 */
export interface SearchOptions {
  enableLogging?: boolean;
  enableDetailedLogging?: boolean;
  maxCandidates?: number;
  improvementThreshold?: number;
}

/**
 * Results from a search operation
 */
export interface SearchResult {
  bestCandidate: Candidate;
  allCandidates: Candidate[];
  searchPath: string[];
  stoppingReason: SearchStoppingReason;
  searchDetails: SearchDetails;
  processingTime: number;
}

/**
 * Detailed information about the search process
 */
export interface SearchDetails {
  startingCandidate: Candidate;
  candidatesGenerated: number;
  directionsExplored?: SearchDirection[];
  chosenDirection?: SearchDirection | null;
  finalMetricValue: number;
  improvementAchieved: number;
}

/**
 * Reasons why a search stopped
 */
export type SearchStoppingReason =
  | 'improvement_stopped'
  | 'efficiency_stopped_improving'
  | 'utilization_stopped_improving'
  | 'no_more_shapes'
  | 'candidate_not_viable'
  | 'no_improvement_found'
  | 'max_candidates_reached'
  | 'already_at_maximum'
  | 'already_at_optimal_efficiency';

/**
 * Base interface that all best-fit strategies must implement.
 * This enables the strategy pattern for different project types.
 */
export interface BestFitStrategy {
  /** Unique identifier for this strategy */
  readonly strategyType: ProjectType;

  /** Human-readable name for logging and debugging */
  readonly strategyName: string;

  /** Brief description of what this strategy optimizes for */
  readonly description: string;

  /**
   * Determines if a candidate meets this strategy's viability criteria.
   * Different strategies may have different requirements beyond basic viability.
   */
  isViable(candidate: Candidate): boolean;

  /**
   * Selects the best first-fit candidate to start the search from.
   * Different strategies may prioritize different metrics (principal vs efficiency).
   */
  selectBestFirstFit(candidates: Candidate[], configuration: Configuration): Candidate | null;

  /**
   * Executes the search algorithm specific to this strategy.
   * Each strategy can implement its own search logic and stopping criteria.
   */
  executeSearch(startingCandidate: Candidate, context: SearchContext): Promise<SearchResult>;

  /**
   * Gets the primary metric this strategy optimizes for.
   * Used for logging and comparison purposes.
   */
  getPrimaryMetric(candidate: Candidate): number;

  /**
   * Gets a human-readable description of the primary metric.
   */
  getPrimaryMetricName(): string;

  /**
   * Validates that the strategy can be applied to the given candidates.
   * Returns null if valid, error message if invalid.
   */
  validateCandidates(candidates: Candidate[]): string | null;
}

/**
 * Options for configuring strategy execution
 */
export interface StrategyExecutionOptions {
  enableLogging?: boolean;
  enableDetailedLogging?: boolean;
  maxCandidates?: number;
  customOptions?: Record<string, unknown>;
}

/**
 * Result from strategy execution
 */
export interface StrategyExecutionResult {
  success: boolean;
  strategy: ProjectType;
  result: SearchResult | null;
  error?: string;
  validationErrors?: string[];
}

/**
 * Configuration for a specific strategy
 */
export interface StrategyConfig {
  strategyType: ProjectType;
  options: StrategyExecutionOptions;
  customParameters?: Record<string, unknown>;
}
